import { Save } from "lucide-react";
import { useMemo } from "react";

import { PageLayout, PageTitle } from "components/layout";
import { Button } from "components/ui/button";
import {
  IDepositOption,
  IDepositOptionState,
  IUpdateStoreDepositOptionBody,
} from "src/@types/store-deposit-option";
import { toast } from "@/components/ui/use-toast";
import {
  useGetStoreDepositOptionAllowedStoresQuery,
  useGetStoreDepositOptionsQuery,
  useUpdateStoreDepositOptionsMutation,
} from "@/services/store-deposit-option";
import { useGetStoresOptionsQuery } from "@/services/store/select-options";
import { layoutClasses } from "@/utils/css-classes";

import { DepositOptionForm } from "./components/deposit-option-form";
import { StoreDepositOptionFilters } from "./components/store-deposit-option-filters";
import { useDepositOptionDraftReducer } from "./reducers/useDepositOptionDraftReducer";
import {
  DepositOptionDraft,
  FilterOption,
  GLOBALLY_HANDLED_STATUS,
  STORE_DEPOSIT_OPTION_LABELS,
  STORE_DEPOSIT_OPTION_MESSAGES,
} from "./types";

const buildDraft = (options: readonly IDepositOption[]): DepositOptionDraft =>
  options.reduce<Record<number, boolean>>(
    (draft: Record<number, boolean>, option: IDepositOption) => ({
      ...draft,
      [option.operationCode]: option.active,
    }),
    {},
  );

export default function StoreDepositOptionPage() {
  const { state: draftState, methods: draftMethods } =
    useDepositOptionDraftReducer();

  const { data: storeOptions } = useGetStoresOptionsQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });
  const { data: allowedStores } = useGetStoreDepositOptionAllowedStoresQuery();

  const allowedCodes: number[] | null = allowedStores?.storeCodes ?? null;

  const availableStores = useMemo(() => {
    const all = (storeOptions ?? []) as FilterOption[];
    if (allowedCodes === null) return all;
    return all.filter((option: FilterOption) =>
      allowedCodes.includes(Number(option.value)),
    );
  }, [storeOptions, allowedCodes]);

  const isStoreLocked: boolean =
    allowedCodes !== null && availableStores.length === 1;

  const storeCode: number | undefined = isStoreLocked
    ? Number(availableStores[0].value)
    : draftState.storeCode;

  const { data: result, isFetching } = useGetStoreDepositOptionsQuery(
    storeCode as number,
    { skip: storeCode === undefined },
  );

  const [updateOptions, { isLoading: isSaving }] =
    useUpdateStoreDepositOptionsMutation();

  const loadedOptions: readonly IDepositOption[] = useMemo(
    () => result?.options ?? [],
    [result],
  );

  const draft: DepositOptionDraft = useMemo(
    () => ({ ...buildDraft(loadedOptions), ...draftState.overrides }),
    [loadedOptions, draftState.overrides],
  );

  const isDirty: boolean = loadedOptions.some(
    (option: IDepositOption) =>
      (draft[option.operationCode] === true) !== option.active,
  );

  const handleToggle = (operationCode: number, active: boolean): void => {
    draftMethods.toggle(operationCode, active);
  };

  const handleSave = async (): Promise<void> => {
    if (storeCode === undefined) return;

    const options: IDepositOptionState[] = loadedOptions.map(
      (option: IDepositOption) => ({
        operationCode: option.operationCode,
        active: draft[option.operationCode] === true,
      }),
    );
    const body: IUpdateStoreDepositOptionBody = { storeCode, options };

    try {
      await updateOptions(body).unwrap();
      draftMethods.markSaved();
      toast(STORE_DEPOSIT_OPTION_MESSAGES.SAVE_SUCCESS);
    } catch (error) {
      const status = (error as { status?: number | string })?.status;
      if (!GLOBALLY_HANDLED_STATUS.has(status as number)) {
        toast(STORE_DEPOSIT_OPTION_MESSAGES.SAVE_ERROR);
      }
    }
  };

  const hasStore: boolean = storeCode !== undefined;

  return (
    <PageLayout>
      <div className={layoutClasses.pageContainer}>
        <PageTitle
          title={STORE_DEPOSIT_OPTION_LABELS.pageTitle}
          description={STORE_DEPOSIT_OPTION_LABELS.pageDescription}
          titleSize="text-3xl"
          descriptionSize="text-xl"
        />

        <StoreDepositOptionFilters
          storeOptions={availableStores}
          storeCode={storeCode}
          isStoreLocked={isStoreLocked}
          onStoreChange={draftMethods.setStore}
        />

        {!hasStore && (
          <p className="mt-4 text-sm text-slate-500">
            {STORE_DEPOSIT_OPTION_LABELS.selectStoreHint}
          </p>
        )}

        {hasStore && (
          <DepositOptionForm
            options={loadedOptions}
            draft={draft}
            isLoading={isFetching}
            isSaving={isSaving}
            onToggle={handleToggle}
          />
        )}

        {hasStore && (
          <div className="flex gap-3 mt-4">
            <Button
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              className="h-8 bg-slate-800 hover:bg-slate-900 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving
                ? STORE_DEPOSIT_OPTION_LABELS.saving
                : STORE_DEPOSIT_OPTION_LABELS.saveButton}
            </Button>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
